export const CATEGORY_LABELS: Record<string, string> = {
  foundation: 'Foundation',
  resilience: 'Resilience',
  connectivity: 'Connectivity',
  workloads: 'Workloads',
  uncategorized: 'Uncategorized',
};

export function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category] || category.charAt(0).toUpperCase() + category.slice(1);
}

/** Turn a slug (`inspector-critical-vulnerability`) into a title-cased phrase. */
export function humanize(s: string): string {
  const spaced = s.replace(/[-_.]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!spaced) return s;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const BARE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Prefixed opaque ids: cust-…, dep-…, oi-…, prod-… — keep verbatim, never humanize.
const PREFIXED_ID = /^[a-z]{2,}-[0-9a-f]{8}(-[0-9a-f]{4,12}){1,4}$/i;

/**
 * Decode a reconciler `resource_id` into human-readable PARTS, one per segment.
 *
 * resource_ids are colon-delimited and shape-varying per reconciler
 * (`customer:rule:event`, `kind:customer:account`, `customer:user@email`, `dep-…`).
 * Rather than parse positionally, resolve EACH segment:
 *  - a known customer id → the customer NAME (via `customerNameById`)
 *  - an unmapped `cust-…`/`dep-…`/`oi-…` opaque id → kept verbatim
 *  - a bare UUID (event instance) → shortened to its first 8 chars
 *  - an email → kept verbatim
 *  - anything else (a rule/kind slug) → `humanize`d
 * Shape-agnostic, so it never misattributes a non-customer segment and degrades
 * gracefully when a customer isn't in the map. The caller decides layout (joined
 * on one line vs stacked per line).
 */
export function humanizeResourceParts(
  resourceId: string,
  customerNameById?: Map<string, string>,
): string[] {
  if (!resourceId) return [];
  return resourceId.split(':').map((seg) => {
    const name = customerNameById?.get(seg);
    if (name) return name;
    if (seg.includes('@')) return seg;
    if (BARE_UUID.test(seg)) return seg.slice(0, 8);
    if (PREFIXED_ID.test(seg)) return seg;
    return humanize(seg);
  });
}

/** Single-line variant: the decoded parts joined with ` · `. */
export function humanizeResourceId(
  resourceId: string,
  customerNameById?: Map<string, string>,
): string {
  if (!resourceId) return resourceId;
  return humanizeResourceParts(resourceId, customerNameById).join(' · ');
}

/** A reconciler's domain type as a human label: `aws-account-baseline` -> `Aws Account Baseline`. */
export function domainLabel(domain: string): string {
  return domain
    .split(/[_-]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
